import moment from 'moment';
import * as Rx from 'rxjs';
import { debounceTime, map, switchMap, take, tap } from 'rxjs/operators';
import { EDateTypes } from 'v2/interfaces/reports/refunds';

import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';
import type { IRefundFilter } from 'v2/interfaces/reports/refunds';

import apiService from '../api';

import type { ApiService } from '../api';

const today = moment().format('DD/MM/YYYY');

export const initialFilter: IRefundFilter = {
  date_type: EDateTypes.PAID,
  date: [today, today],
  invoice_id: '',
  product_id: [],
  motive_id: [],
};

export const initialPagination: IPaginationParams = {
  current_page: 1,
  per_page: 10,
};

export class RefundsService {
  private filters$ = new Rx.BehaviorSubject<IRefundFilter>(initialFilter);

  private loadingRefunds$ = new Rx.BehaviorSubject<boolean>(true);
  private refunds$ = new Rx.BehaviorSubject<any[]>([]);

  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>(initialPagination);
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);

  private totalItems$ = new Rx.BehaviorSubject<number>(0);
  private totalRefunded$ = new Rx.BehaviorSubject<number>(0);

  constructor(private apiService: ApiService) {}

  public setFilter(filter: IRefundFilter): void {
    this.loadingRefunds$.next(true);
    this.filters$.pipe(take(1)).subscribe((currentFilter: IRefundFilter) => {
      this.filters$.next({ ...currentFilter, ...filter });
    });
  }

  public getTotalItems = (): Rx.Observable<number> => this.totalItems$;
  public getTotalRefunded = () => this.totalRefunded$;
  public getFilters = (): Rx.Observable<IRefundFilter> => this.filters$;
  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;
  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;

  public setPagination(pagination: IPaginationParams): void {
    this.paginationParams$.next(pagination);
  }

  public resetPage = () => {
    this.paginationParams$.next({ ...this.paginationParams$.value, current_page: 1 });
    return this.getPaginationParams();
  };

  public isLoading = () => this.loadingRefunds$;

  public getRefunds = (): Rx.Observable<any[]> => this.refunds$;

  public formatFilter = (filter: IRefundFilter) => {
    const newFilter: Partial<IRefundFilter> = {};
    const [start, end] = filter.date;
    newFilter[filter.date_type] = start;
    newFilter[`${filter.date_type}_final`] = end;
    filter.product_id && (newFilter.product_id = filter.product_id);
    filter.invoice_id && (newFilter.invoice_id = filter.invoice_id);
    filter.motive_id.length && (newFilter.motive_id = filter.motive_id);
    return newFilter;
  };

  public loadRefunds() {
    return this.getFilters().pipe(
      map((filter) => this.formatFilter(filter)),
      switchMap((filter) => this.resetPage().pipe(map((params) => ({ ...filter, ...params })))),
      debounceTime(10),
      switchMap((filter) => {
        this.loadingRefunds$.next(true);
        return this.apiService.post('reports/sales/refunded', filter);
      }),
      tap((response) => {
        this.refunds$.next(response.data);
        this.totalRefunded$.next(response.total_refunded);
        this.paginationResponse$.next(response.pagination);
        this.totalItems$.next(response.pagination.total_items);
        this.loadingRefunds$.next(false);
      })
    );
  }

  public export() {
    return this.apiService.post('reports/sales/refunded', { ...this.formatFilter(this.filters$.value), export: true });
  }

  public cleanFilter() {
    this.filters$.next(initialFilter);
  }
}

export const refundsService = new RefundsService(apiService);
